import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { backend_url } from "../App";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // Check authentication status on app load
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = localStorage.getItem("auth-token");
        if (token) {
          // Verify token with backend
          const response = await fetch(`${backend_url}/admin/verify-token`, {
            method: "GET",
            headers: {
              "auth-token": token,
              "Content-Type": "application/json",
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setIsAuthenticated(true);
              setUser(data.user || { email: "admin" });
            } else {
              // Token is invalid, remove it
              localStorage.removeItem("auth-token");
              setIsAuthenticated(false);
              setUser(null);
            }
          } else {
            // Token verification failed, remove it
            localStorage.removeItem("auth-token");
            setIsAuthenticated(false);
            setUser(null);
          }
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        // On error, assume not authenticated
        localStorage.removeItem("auth-token");
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email, password) => {
    try {
      const loginData = {
        admemail: email,
        admpassword: password,
      };

      const response = await fetch(`${backend_url}/admlogin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        mode: "cors",
        body: JSON.stringify(loginData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorText}`
        );
      }

      const data = await response.json();

      if (data && data.success) {
        localStorage.setItem("auth-token", data.token);
        setIsAuthenticated(true);
        setUser(data.user || { email });
        navigate("/admin");
        return { success: true };
      } else {
        return {
          success: false,
          error: data.errors || "Login failed. Please check your credentials.",
        };
      }
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        error: `Login failed: ${error.message}`,
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("auth-token");
    setIsAuthenticated(false);
    setUser(null);
    navigate("/");
  };

  const signup = async (userData) => {
    try {
      const response = await fetch(`${backend_url}/admsignup`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem("auth-token", data.token);
        setIsAuthenticated(true);
        setUser(data.user || { email: userData.admemail });
        navigate("/admin");
        return { success: true };
      } else {
        return {
          success: false,
          error: data.errors || "Signup failed.",
        };
      }
    } catch (error) {
      console.error("Signup error:", error);
      return {
        success: false,
        error: `Signup failed: ${error.message}`,
      };
    }
  };

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    signup,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
